<template>
  <div>
    <survey-type-list-add-new 
      :is-add-new-sidebar-active.sync="isAddNewSidebarActive"
      @new-record-created="newRecordCreated" 
      @refetch-data="refetchData" 
      :key="currentId" 
    />
    
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>{{ $t('Show') }}</label>
            <v-select 
              v-model="perPage" 
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" 
              :options="perPageOptions"
              :clearable="false" 
              class="per-page-selector d-inline-block mx-50" 
            />
            <label>{{ $t('entries') }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input 
                v-model="searchQuery" 
                class="d-inline-block mr-1" 
                :placeholder="$t('Search...')" 
              />
              <b-button variant="primary" @click="isAddNewSidebarActive = true">
                <span class="text-nowrap">{{ $t("surveyType.create") }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table 
        ref="refSurveyTypeListTable" 
        class="position-relative" 
        :items="filteredSurveyTypes" 
        responsive 
        :fields="tableColumns"
        primary-key="id" 
        :sort-by.sync="sortBy" 
        show-empty 
        :empty-text="$t('No matching records found')"
        :sort-desc.sync="isSortDirDesc"
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #head()="data">
          <span class="text">{{ $t(data.label.toUpperCase()) }}</span>
        </template>

        <template #cell(title)="data">
          <b-media vertical-align="center">
            <b-link 
              :to="{ 
                name: 'SurveyTypeEdit', 
                params: { id: data.item.id },
                query: { 
                  title: data.item.title,
                  typekey: data.item.typekey,
                  description: data.item.description 
                }
              }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.title }}
            </b-link>
            <small class="text-muted">{{ data.item.typekey }}</small>
          </b-media>
        </template>

        <template #cell(typekey)="data">
          <b-badge pill variant="light-primary" class="text-capitalize">
            {{ data.item.typekey }}
          </b-badge>
        </template>

        <template #cell(description)="data">
          <span class="text-muted">{{ data.item.description || '-' }}</span>
        </template>


        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item 
              :to="{ 
                name: 'SurveyTypeEdit', 
                params: { id: data.item.id },
                query: { 
                  title: data.item.title,
                  typekey: data.item.typekey,
                  description: data.item.description 
                }
              }"
              variant="primary"
            >
              <feather-icon icon="EditIcon" class="text-primary" />
              <span class="align-middle ml-50">{{ $t("Edit") }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteSurveyType(data.item)" variant="danger">
              <feather-icon icon="TrashIcon" class="text-danger" />
              <span class="align-middle ml-50 text-danger">{{ $t("Delete") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              {{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }} {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}
            </span>
          </b-col>
          
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination 
              v-model="currentPage" 
              :total-rows="totalSurveyTypes" 
              :per-page="perPage" 
              first-number 
              last-number
              class="mb-0 mt-1 mt-sm-0" 
              prev-class="prev-item" 
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import store from '@/store'
import axios from '@axios'
import { ref, onUnmounted, onMounted } from '@vue/composition-api'
import useSurveyTypesList from './useSurveyTypesList'
import surveyTypeStoreModule from '../surveyTypeStoreModule'
import SurveyTypeListAddNew from './SurveyTypeListAddNew.vue'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"

export default {
  components: {
    SurveyTypeListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationContent
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      currentId: 0,
    }
  },
  methods: {
    newRecordCreated() {
      this.currentId++
    },
    
    deleteSurveyType(surveyType) {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t('You won\'t be able to revert this!'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store
            .dispatch('app-survey-types/deleteSurveyType', {
              id: surveyType.id,
            })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$t('Success'),
                  icon: "CheckIcon",
                  variant: "success",
                },
              })
              this.refetchData()
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$t('Error'),
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              })
            })
        }
      })
    }
  },
  setup() {
    const SURVEY_TYPE_APP_STORE_MODULE_NAME = 'app-survey-types'

    // Register module
    if (!store.hasModule(SURVEY_TYPE_APP_STORE_MODULE_NAME)) {
      store.registerModule(SURVEY_TYPE_APP_STORE_MODULE_NAME, surveyTypeStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SURVEY_TYPE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(SURVEY_TYPE_APP_STORE_MODULE_NAME)
      }
    })

    const isAddNewSidebarActive = ref(false)

    const {
      fetchSurveyTypes,
      filteredSurveyTypes,
      tableColumns,
      perPage,
      currentPage,
      totalSurveyTypes,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSurveyTypeListTable,
      refetchData,
      resolveStatusVariant,
      resolveStatusText,
    } = useSurveyTypesList()

    // Fetch data on mount
    onMounted(() => {
      fetchSurveyTypes()
    })

    return {
      isAddNewSidebarActive,
      fetchSurveyTypes,
      filteredSurveyTypes,
      tableColumns,
      perPage,
      currentPage,
      totalSurveyTypes,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSurveyTypeListTable,
      refetchData,
      resolveStatusVariant,
      resolveStatusText,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

